import type { DataTableCell, DataTableColumn, DataTableColumnGroup, DataTableRow, DataTableRowLimit, DataTableSort } from '@ui/chart/DataTable';
import type { BarChartSeries } from '@ui/chart/BarChart';
import {
	RateBoard, RatePlanEvolution, RatePlanFigures, RatePlanPart, RatePlanReport, RefundPolicy, RevenueScope, SITUATION_DAYS, SituationDays,
	VatBasis,
} from '@app/model/bi/Bi';
import { occupiedOf, revenueOf } from '../Bi.utilities';

// The figures of the Rate Plans report (BI report 3). The formulas are those of the spec (Rate Plans Feature
// Specification v1, chapter 7) with the grill decisions of 2026-10-21: every figure of a group, a rate plan of several
// properties or the total is a sum of its parts, and every ratio comes from those sums, never from an average of rows.

export type GroupBy = 'POLICY_BOARD' | 'POLICY' | 'BOARD' | 'NONE';
// The blocks of the Columns menu (RQ-15). TODAY always shows.
export type ColumnSet = 'ly' | 'd1' | 'd3' | 'd7' | 'd14' | 'd30' | 'pu1' | 'pu3' | 'pu7' | 'pu14' | 'pu30';
// The measure of the build-up chart (ROO2-267, M-11).
export type EvolutionMeasure = 'rn' | 'rev' | 'adr';

// RQ-08: the fixed order of the groups, never by size.
export const POLICIES: RefundPolicy[] = ['NON_REFUNDABLE', 'FLEXIBLE', 'PROMOTIONS', 'OTHER'];
export const BOARDS: RateBoard[] = ['MEALS', 'ROOM_ONLY', 'NO_BOARD'];
export const POLICY_LABEL: Record<RefundPolicy, string> = {
	NON_REFUNDABLE: 'Non-refundable', FLEXIBLE: 'Flexible', PROMOTIONS: 'Promotions & offers', OTHER: 'Other rate plans',
};
export const BOARD_LABEL: Record<RateBoard, string> = { MEALS: 'Breakfast or meals', ROOM_ONLY: 'Room only', NO_BOARD: 'No board' };
export const NO_RATE_PLAN = 'No rate plan';
// RQ-12: the first rows of a list.
export const ROW_LIMIT = 15;
// Each cut list names what it cuts. Grouped: level 1 = the rate plans of a group, level 2 = the properties of a rate plan
// (All properties). None: level 1 = the properties of a rate plan.
export const rowLimitOf = (by: GroupBy): DataTableRowLimit => {
	const plans = (level: number) => level === 0 || (by !== 'NONE' && level === 1);
	return {
		count: ROW_LIMIT,
		moreLabel: (hidden, _all, level) => level === 0 ? `+ ${hidden} more rate plans, included in the total`
			: plans(level) ? `+ ${hidden} more rate plans, included in the group's row` : `+ ${hidden} more properties, included in the rate plan's row`,
		showAllLabel: (all, level) => plans(level) ? `Show all ${all} rate plans` : `Show all ${all} properties`,
		showTopLabel: `Show the top ${ROW_LIMIT}`,
	};
};
// RQ-20: the lines of the build-up chart; the rest is one line "Other (N)".
export const EVOLUTION_LINES = 6;
const DAYS: SituationDays[] = [1, 3, 7, 14, 30];

const ZERO: RatePlanFigures = { roomNights: 0, occupiedRooms: 0, roomRevenue: 0, roomRevenueNet: 0, totalRevenue: 0, totalRevenueNet: 0 };
const cents = (v: number) => Math.round(v * 100) / 100;
const plus = (a: number | null, b: number | null): number | null => a === null || b === null ? null : cents(a + b);
// A sum of figures: a revenue is null when one of its parts is null (never a partial sum, ROO2-236).
export const add = (a: RatePlanFigures, b: RatePlanFigures): RatePlanFigures => ({
	roomNights: a.roomNights + b.roomNights, occupiedRooms: occupiedOf(a) + occupiedOf(b), roomRevenue: plus(a.roomRevenue, b.roomRevenue), roomRevenueNet: plus(a.roomRevenueNet, b.roomRevenueNet),
	totalRevenue: cents(a.totalRevenue + b.totalRevenue), totalRevenueNet: plus(a.totalRevenueNet, b.totalRevenueNet),
});

// The sums of a row: today and the situations D-1 … D-30 (SITUATION_DAYS), STLY, LY actual, and today's figures of the
// properties that have stays in the last-year period (decision 16: the base of Δ%).
export type Sums = { cuts: RatePlanFigures[], stly: RatePlanFigures, ly: RatePlanFigures, comparable: RatePlanFigures };
const emptySums = (): Sums => ({ cuts: SITUATION_DAYS.map(() => ZERO), stly: ZERO, ly: ZERO, comparable: ZERO });
const addPart = (s: Sums, p: RatePlanPart, missing: Set<number>): Sums => ({
	cuts: SITUATION_DAYS.map((d, i) => add(s.cuts[i], p.situations.find(x => x.daysBefore === d) ?? ZERO)),
	stly: add(s.stly, p.stly), ly: add(s.ly, p.lyActual),
	comparable: missing.has(p.companyHotelId) ? s.comparable : add(s.comparable, p.situations.find(x => x.daysBefore === 0) ?? ZERO),
});
const sumOf = (parts: RatePlanPart[], missing: Set<number>): Sums => parts.reduce((s, p) => addPart(s, p, missing), emptySums());

export type RatePlanNode = {
	id: string,
	kind: 'GROUP' | 'RATE_PLAN' | 'PROPERTY',
	title: string,
	// A group: its rate plans; a rate plan: its properties (RQ-10).
	count: number,
	muted: boolean,
	// A property with no stays in the last-year period: its last-year columns have no value (decision 16).
	noLastYear: boolean,
	sums: Sums,
	children: RatePlanNode[],
};

export type RatePlanTree = {
	// The groups in the fixed order (RQ-08), or the rate plans (Group by None), with REV of today, largest first.
	rows: RatePlanNode[],
	total: Sums,
	// The rate plans of the scope: Total (N).
	plans: number,
};

// The group of a part, with its place in the fixed order of RQ-08. null: Group by None.
export const groupOf = (p: Pick<RatePlanPart, 'policy' | 'board'>, by: GroupBy): { id: string, title: string, order: number } | null => {
	const pol = POLICIES.indexOf(p.policy), brd = BOARDS.indexOf(p.board);
	if (by === 'POLICY_BOARD') return { id: `${p.policy}:${p.board}`, title: `${POLICY_LABEL[p.policy]} · ${BOARD_LABEL[p.board]}`, order: pol * 10 + brd };
	if (by === 'POLICY') return { id: p.policy, title: POLICY_LABEL[p.policy], order: pol };
	if (by === 'BOARD') return { id: p.board, title: BOARD_LABEL[p.board], order: brd };
	return null;
};

const todayOf = (p: RatePlanPart): RatePlanFigures => p.situations.find(x => x.daysBefore === 0) ?? ZERO;
const revOrZero = (f: RatePlanFigures, scope: RevenueScope, vat: VatBasis) => revenueOf(f, scope, vat) ?? 0;
const byRevenue = (scope: RevenueScope, vat: VatBasis) => (a: RatePlanNode, b: RatePlanNode): number => {
	const diff = revOrZero(b.sums.cuts[0], scope, vat) - revOrZero(a.sums.cuts[0], scope, vat);
	return diff !== 0 ? diff : a.title.localeCompare(b.title);
};
// The spelling of a rate plan: the title of its part with the most room nights today.
const titleOf = (parts: RatePlanPart[]) => {
	const best = [...parts].sort((a, b) => todayOf(b).roomNights - todayOf(a).roomNights)[0];
	return best.key === '' ? NO_RATE_PLAN : best.title;
};

// The table tree of RQ-10: groups → rate plans → properties (withProperties: All properties), or rate plans → properties.
export const buildTree = (report: RatePlanReport, by: GroupBy, nameOf: (companyHotelId: number) => string, withProperties: boolean,
	scope: RevenueScope, vat: VatBasis): RatePlanTree => {
	const missing = new Set(report.lastYearMissing);
	const order = byRevenue(scope, vat);
	const planNode = (prefix: string, key: string, parts: RatePlanPart[]): RatePlanNode => {
		const hotels = new Map<number, RatePlanPart[]>();
		parts.forEach(p => hotels.set(p.companyHotelId, [...(hotels.get(p.companyHotelId) ?? []), p]));
		const children = withProperties ? [...hotels.entries()].map(([id, ps]): RatePlanNode => ({
			id: `${prefix}|${key}|${id}`, kind: 'PROPERTY', title: nameOf(id), count: 0, muted: false, noLastYear: missing.has(id),
			sums: sumOf(ps, missing), children: [],
		})).sort(order) : [];
		return { id: `${prefix}|${key}`, kind: 'RATE_PLAN', title: titleOf(parts), count: hotels.size, muted: key === '', noLastYear: false,
			sums: sumOf(parts, missing), children };
	};
	const plansOf = (prefix: string, parts: RatePlanPart[]): RatePlanNode[] => {
		const byKey = new Map<string, RatePlanPart[]>();
		parts.forEach(p => byKey.set(p.key, [...(byKey.get(p.key) ?? []), p]));
		return [...byKey.entries()].map(([key, ps]) => planNode(prefix, key, ps)).sort(order);
	};
	const plans = new Set(report.parts.map(p => p.key)).size;
	const total = sumOf(report.parts, missing);
	if (by === 'NONE') return { rows: plansOf('none', report.parts), total, plans };
	const groups = new Map<string, { title: string, order: number, parts: RatePlanPart[] }>();
	report.parts.forEach(p => {
		const g = groupOf(p, by) ?? { id: 'none', title: '', order: 0 };
		const entry = groups.get(g.id) ?? { title: g.title, order: g.order, parts: [] };
		entry.parts.push(p);
		groups.set(g.id, entry);
	});
	const rows = [...groups.entries()].sort((a, b) => a[1].order - b[1].order).map(([id, g]): RatePlanNode => {
		const children = plansOf(id, g.parts);
		return { id, kind: 'GROUP', title: g.title, count: children.length, muted: false, noLastYear: false, sums: sumOf(g.parts, missing), children };
	});
	return { rows, total, plans };
};

const ratio = (a: number | null, b: number | null): number | null => a === null || b === null || b === 0 ? null : a / b;

// M-07: (REV now − last year's REV) ÷ last year's REV. "new" when last year's REV is 0 and now is not; null (n/a) when
// both are 0 or a revenue has no value. "now" is the revenue of the properties with last-year stays (decision 16).
export const change = (now: number | null, lastYear: number | null): number | 'new' | null => {
	if (now === null || lastYear === null) return null;
	if (lastYear === 0) return now === 0 ? null : 'new';
	return (now - lastYear) / lastYear;
};

// The cells of a row of the table (RQ-15). shareOf: the revenue that SHARE divides by (the total; for a property row, the
// revenue of its rate plan). build: the BUILD-UP line of the row (ROO2-267, M-12: one point per day of the window), null when
// the row has none.
export const cellsOf = (node: Pick<RatePlanNode, 'sums' | 'noLastYear'>, title: string, shareOf: number | null, scope: RevenueScope,
	vat: VatBasis, build: (number | null)[] | null = null): Record<string, DataTableCell> => {
	const s = node.sums;
	const rev = (f: RatePlanFigures) => revenueOf(f, scope, vat);
	const now = s.cuts[0];
	const cells: Record<string, DataTableCell> = {
		name: title, rn: now.roomNights, rev: rev(now), adr: occupiedOf(now) === 0 ? null : ratio(rev(now), occupiedOf(now)),
		share: ratio(rev(now), shareOf), build,
		// Like-for-like (ROO2-271 D1): today without the properties that have no stays in the
		// last-year period, the base of Δ%. It shows when the scope has such a property (columnsOf, lfl).
		lflRn: node.noLastYear ? null : s.comparable.roomNights, lflRev: node.noLastYear ? null : rev(s.comparable),
		stlyRn: node.noLastYear ? null : s.stly.roomNights, stlyRev: node.noLastYear ? null : rev(s.stly),
		vsStly: node.noLastYear ? null : change(rev(s.comparable), rev(s.stly)),
		lyRn: node.noLastYear ? null : s.ly.roomNights, lyRev: node.noLastYear ? null : rev(s.ly),
		vsLy: node.noLastYear ? null : change(rev(s.comparable), rev(s.ly)),
	};
	const adrNow = occupiedOf(now) === 0 ? null : ratio(rev(now), occupiedOf(now));
	DAYS.forEach(d => {
		const past = s.cuts[SITUATION_DAYS.indexOf(d)];
		const pastRev = rev(past), nowRev = rev(now);
		const puRn = now.roomNights - past.roomNights;
		const puRev = nowRev === null || pastRev === null ? null : cents(nowRev - pastRev);
		const adrPast = occupiedOf(past) === 0 ? null : ratio(pastRev, occupiedOf(past));
		cells[`d${d}Rn`] = past.roomNights;
		cells[`d${d}Rev`] = pastRev;
		cells[`d${d}Adr`] = adrPast;
		cells[`pu${d}Rn`] = puRn;
		cells[`pu${d}Rev`] = puRev;
		// ADR Δ (ROO2-271 D3): ADR today − ADR at situation D-n; no value when one of the two has no room
		// nights. It replaces PU ADR (PU REV ÷ PU RN), which was negative or very high when nights were cancelled.
		cells[`pu${d}Adr`] = adrNow === null || adrPast === null ? null : cents(adrNow - adrPast);
	});
	return cells;
};

// The columns and their header blocks (RQ-15), in the order of the spec. buildUp (ROO2-267): the label of the BUILD-UP block
// ("Build-up 30D") and of its column ("RN" or "REV"), a small line (the DataTable format sparkline, red when it fell).
// lfl (ROO2-271 D1): the scope has a property with no stays in the last-year period; the previous-year
// block then starts with today like-for-like, the figures that Δ% compares.
export const columnsOf = (sets: ColumnSet[], buildUp: { block: string, label: string } | null = null, lfl = false)
	: { columns: DataTableColumn[], groups: DataTableColumnGroup[] } => {
	const columns: DataTableColumn[] = [{ id: 'name', label: 'Rate plan' },
		{ id: 'rn', label: 'RN', format: 'number' }, { id: 'rev', label: 'REV', format: 'money' },
		{ id: 'adr', label: 'ADR', format: 'money', decimals: 2 }, { id: 'share', label: 'SHARE', format: 'percent', decimals: 1 }];
	const groups: DataTableColumnGroup[] = [{ label: '', span: 1 }, { label: 'Today', span: 4 }];
	if (buildUp !== null) {
		columns.push({ id: 'build', label: buildUp.label, format: 'sparkline', valueFormat: buildUp.label === 'REV' ? 'money' : 'number' });
		groups.push({ label: buildUp.block, span: 1 });
	}
	if (sets.includes('ly')) {
		if (lfl) columns.push({ id: 'lflRn', label: 'TODAY LFL RN', format: 'number' }, { id: 'lflRev', label: 'TODAY LFL REV', format: 'money' });
		columns.push({ id: 'stlyRn', label: 'STLY RN', format: 'number' }, { id: 'stlyRev', label: 'STLY REV', format: 'money' },
			{ id: 'vsStly', label: 'Δ%', format: 'percent', decimals: 1, signed: true, tone: 'change' },
			{ id: 'lyRn', label: 'LY ACTUAL RN', format: 'number' }, { id: 'lyRev', label: 'LY ACTUAL REV', format: 'money' },
			{ id: 'vsLy', label: 'Δ%', format: 'percent', decimals: 1, signed: true, tone: 'change' });
		groups.push({ label: lfl ? 'Previous year · like-for-like' : 'Previous year', span: lfl ? 8 : 6 });
	}
	DAYS.filter(d => sets.includes(`d${d}` as ColumnSet)).forEach(d => {
		columns.push({ id: `d${d}Rn`, label: 'RN', format: 'number' }, { id: `d${d}Rev`, label: 'REV', format: 'money' },
			{ id: `d${d}Adr`, label: 'ADR', format: 'money', decimals: 2 });
		groups.push({ label: `Situation D-${d}`, span: 3 });
	});
	DAYS.filter(d => sets.includes(`pu${d}` as ColumnSet)).forEach(d => {
		columns.push({ id: `pu${d}Rn`, label: 'RN', format: 'number', signed: true, tone: 'change' },
			{ id: `pu${d}Rev`, label: 'REV', format: 'money', signed: true, tone: 'change' },
			{ id: `pu${d}Adr`, label: 'ADR Δ', format: 'money', decimals: 2, signed: true, tone: 'change' });
		groups.push({ label: `PU ${d}`, span: 3 });
	});
	return { columns, groups };
};

// The DataTable rows of the tree. A group shows its count of rate plans; a rate plan of All properties its count of
// properties (RQ-10). buildOf gives the BUILD-UP line of a group or rate plan row (ROO2-267); a property row has none.
export const tableRows = (tree: RatePlanTree, scope: RevenueScope, vat: VatBasis,
	buildOf: (node: RatePlanNode) => (number | null)[] | null = () => null): DataTableRow[] => {
	const totalRev = revenueOf(tree.total.cuts[0], scope, vat);
	const row = (n: RatePlanNode): DataTableRow => {
		const planRev = revenueOf(n.sums.cuts[0], scope, vat);
		return {
			id: n.id, muted: n.muted,
			cells: cellsOf(n, n.title, totalRev, scope, vat, buildOf(n)),
			...(n.children.length > 0 ? {
				note: `· ${n.count}`,
				children: n.children.map(c => c.kind === 'PROPERTY'
					? { id: c.id, cells: cellsOf(c, c.title, planRev, scope, vat) }
					: row(c)),
			} : {}),
		};
	};
	return tree.rows.map(row);
};

export const totalRow = (tree: RatePlanTree, scope: RevenueScope, vat: VatBasis): DataTableRow =>
	({ id: 'total', cells: cellsOf({ sums: tree.total, noLastYear: false }, `Total (${tree.plans})`, revenueOf(tree.total.cuts[0], scope, vat), scope, vat) });

// --- headline figures and the mix (direction B) -----------------------------------------------------------------------

// The share of the revenue of a set of parts, today (all properties) and for the last-year columns.
export const shareChange = (report: RatePlanReport, pick: (p: RatePlanPart) => boolean, scope: RevenueScope, vat: VatBasis) => {
	const missing = new Set(report.lastYearMissing);
	const all = sumOf(report.parts, missing), part = sumOf(report.parts.filter(pick), missing);
	const rev = (f: RatePlanFigures) => revenueOf(f, scope, vat);
	const now = ratio(rev(part.cuts[0]), rev(all.cuts[0]));
	// The change in points against STLY, like for like: the properties with last-year stays only.
	const comparable = ratio(rev(part.comparable), rev(all.comparable));
	const stly = ratio(rev(part.stly), rev(all.stly));
	return { now, points: comparable === null || stly === null ? null : comparable - stly };
};

// The revenue mix: one stacked bar each for this year, STLY and LY actual, one series per group in the fixed order.
export const mixSeries = (report: RatePlanReport, by: 'POLICY' | 'BOARD', scope: RevenueScope, vat: VatBasis): BarChartSeries[] => {
	const missing = new Set(report.lastYearMissing);
	const all = sumOf(report.parts, missing);
	const rev = (f: RatePlanFigures) => revenueOf(f, scope, vat);
	const ids: string[] = by === 'POLICY' ? POLICIES : BOARDS;
	return ids.map(id => {
		const s = sumOf(report.parts.filter(p => (by === 'POLICY' ? p.policy : p.board) === id), missing);
		return { id, label: by === 'POLICY' ? POLICY_LABEL[id as RefundPolicy] : BOARD_LABEL[id as RateBoard],
			values: [ratio(rev(s.cuts[0]), rev(all.cuts[0])), ratio(rev(s.stly), rev(all.stly)), ratio(rev(s.ly), rev(all.ly))] };
	}).filter(s => s.values.some(v => v !== null && v > 0));
};

// --- Excel export (decisions 17–20, the export of ROO2-261) -------------------------------------------------------------

const ALL_SETS: ColumnSet[] = ['ly', 'd1', 'd3', 'd7', 'd14', 'd30', 'pu1', 'pu3', 'pu7', 'pu14', 'pu30'];
// The column sets of the file: the ones that the user sees, in the order of the table, with TODAY first (decision 17).
export const exportColumns = (sets: ColumnSet[]): string[] => ['today', ...ALL_SETS.filter(s => sets.includes(s))];

// --- build-up (ROO2-267, spec chapter 5) --------------------------------------------------------------------------------

// One year of a line: the room nights and the revenue of the switches on each day of the window.
// occ: the occupied rooms by the report rules (ROO2-315 part c2, = rn without rules), which ADR divides by.
export type EvolutionValues = { rn: number[], occ: number[], rev: (number | null)[] };
export type EvolutionLine = { id: string, title: string, thisYear: EvolutionValues, lastYear: EvolutionValues };
export const OTHER_LINE = 'other';

const plusValues = (a: EvolutionValues, b: EvolutionValues): EvolutionValues => ({
	rn: a.rn.map((v, i) => v + b.rn[i]), occ: a.occ.map((v, i) => v + b.occ[i]), rev: a.rev.map((v, i) => plus(v, b.rev[i])),
});
const zeroValues = (days: number): EvolutionValues => ({
	rn: Array.from({ length: days }, () => 0), occ: Array.from({ length: days }, () => 0), rev: Array.from({ length: days }, () => 0),
});

// M-11 for each row of the table that can have a line: a group (its id), a rate plan in a group (group|key) and a rate plan of
// Group by None (none|key), the ids of buildTree. A group is the sum of its rate plans. A property row has no line.
export const evolutionByRow = (evolution: RatePlanEvolution, by: GroupBy): Map<string, { thisYear: EvolutionValues, lastYear: EvolutionValues }> => {
	const out = new Map<string, { thisYear: EvolutionValues, lastYear: EvolutionValues }>();
	const n = evolution.days.length;
	const addTo = (id: string, thisYear: EvolutionValues, lastYear: EvolutionValues) => {
		const old = out.get(id) ?? { thisYear: zeroValues(n), lastYear: zeroValues(n) };
		out.set(id, { thisYear: plusValues(old.thisYear, thisYear), lastYear: plusValues(old.lastYear, lastYear) });
	};
	evolution.series.forEach(s => {
		const thisYear = { rn: s.thisYearRn, occ: s.thisYearOccupied ?? s.thisYearRn, rev: s.thisYearRev };
		const lastYear = { rn: s.lastYearRn, occ: s.lastYearOccupied ?? s.lastYearRn, rev: s.lastYearRev };
		const group = groupOf(s, by);
		if (group === null) addTo(`none|${s.key}`, thisYear, lastYear);
		else {
			addTo(group.id, thisYear, lastYear);
			addTo(`${group.id}|${s.key}`, thisYear, lastYear);
		}
	});
	return out;
};

// The lines of the chart (RQ-20): one per row of the table in its current order (order: the ids of the top rows after a
// sort, onSortChange), the first EVOLUTION_LINES, and "Other (N)" for the rest added together. With None, the rest is every
// other rate plan, listed or not.
export const evolutionLines = (evolution: RatePlanEvolution, tree: RatePlanTree, by: GroupBy, order: string[] | null = null)
	: { lines: EvolutionLine[], other: EvolutionLine | null } => {
	const byRow = evolutionByRow(evolution, by);
	const n = evolution.days.length;
	const nodes = order === null ? tree.rows
		: order.map(id => tree.rows.find(r => r.id === id)).filter((r): r is RatePlanNode => r !== undefined);
	const rows = nodes.map(r => ({ id: r.id, title: r.title, ...(byRow.get(r.id) ?? { thisYear: zeroValues(n), lastYear: zeroValues(n) }) }));
	const rest = rows.slice(EVOLUTION_LINES);
	const other = rest.length === 0 ? null : {
		id: OTHER_LINE, title: `Other (${rest.length})`,
		thisYear: rest.reduce((v, r) => plusValues(v, r.thisYear), zeroValues(n)),
		lastYear: rest.reduce((v, r) => plusValues(v, r.lastYear), zeroValues(n)),
	};
	return { lines: rows.slice(0, EVOLUTION_LINES), other };
};

// RQ-20 with Group by None: the ids of the top rows in the order of a sort of the table, by the rule of the DataTable (design
// system v1.22.1, which does not export it): a number before a text ("new") before a missing value, in both orders; a
// sparkline by its change. The page reads it again from the current rows, so after a revenue switch, the measure or the
// window, which the table sorts again, the chart still has the order of the table.
export const topRowOrder = (rows: DataTableRow[], sort: DataTableSort, columns: DataTableColumn[]): string[] => {
	const format = columns.find(c => c.id === sort.columnId)?.format;
	const numbers = format === 'money' || format === 'percent' || format === 'number' || format === 'points';
	const change = (values: (number | null)[]) => {
		const known = values.filter((v): v is number => v !== null && !Number.isNaN(v));
		return known.length < 2 ? null : known[known.length - 1] - known[0];
	};
	const valueOf = (v: DataTableCell) => Array.isArray(v) ? change(v) : v;
	const rank = (v: DataTableCell) => {
		const value = valueOf(v);
		if (value === null || value === undefined || value === '') return 2;
		return typeof value === 'string' && numbers && Number.isNaN(Number(value)) ? 1 : 0;
	};
	const factor = sort.direction === 'asc' ? 1 : -1;
	return [...rows].sort((r1, r2) => {
		const a = r1.cells[sort.columnId], b = r2.cells[sort.columnId];
		if (rank(a) !== 0 || rank(b) !== 0) return rank(a) - rank(b);
		const x = valueOf(a), y = valueOf(b);
		return (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y))) * factor;
	}).map(row => row.id);
};

// M-11 for each point of a line: RN, the revenue of the switches, or ADR from the sums: revenue ÷ occupied rooms (ROO2-315
// part c2; no value when there is no occupied room).
export const pointsOf = (values: EvolutionValues, measure: EvolutionMeasure): (number | null)[] => values.rn.map((rn, i) => {
	if (measure === 'rn') return rn;
	const rev = values.rev[i];
	const occ = values.occ[i];
	return measure === 'rev' ? rev : (occ === 0 ? null : ratio(rev, occ));
});

// M-12: the last point of the line − its first point; null with fewer than 2 points.
export const buildUpOf = (points: (number | null)[]): number | null => {
	const values = points.filter((v): v is number => v !== null);
	return values.length < 2 ? null : cents(values[values.length - 1] - values[0]);
};

// M-13: the figures above a focused chart. No change % when last year's point is 0, nothing but Now and LY for ADR; the 7-day
// figures need at least 8 points.
export const focusFigures = (thisYear: (number | null)[], lastYear: (number | null)[], measure: EvolutionMeasure) => {
	const last = thisYear.length - 1;
	const now = thisYear[last] ?? null, before = lastYear[last] ?? null;
	const week = (line: (number | null)[]) => last < 7 || line[last] === null || line[last - 7] === null ? null
		: (line[last] as number) - (line[last - 7] as number);
	return {
		now, lastYear: before,
		change: measure === 'adr' || now === null || before === null || before === 0 ? null : (now - before) / before,
		week: measure === 'adr' ? null : week(thisYear),
		weekLastYear: measure === 'adr' ? null : week(lastYear),
	};
};
